import { WIZARD_QUESTION_IDS } from '../../constants.js';
import type { ConnectedAnswers, LocationGrounding, TimePeriod, WorldDraft } from '../../domain/types.js';
import { isNonBlankValue } from '../../domain/types.js';
import {
  defaultRhythmSuggestion,
  suggestForQuestion,
  suggestGroundingField,
  suggestTimePeriod,
} from './fallbacks.js';
import { setConnected, setLocationGrounding, setTimePeriod } from './draftReducer.js';
import {
  DEFAULT_GROUNDING_LANGUAGE,
  displayGroundingLanguage,
  emptyConnectedAnswers,
  patchConnectedCrowd,
} from './questionSchema.js';
import { DEFAULT_PERIOD_PRESENT, trimTimePeriod } from './periodStep.js';

export interface OptionalBlankRef {
  fieldId: string;
  label: string;
}

export type OptionalBuilderStep = 'location-grounding' | 'connected' | 'time-period';

export function isLanguageOptionalBlank(
  location: LocationGrounding | null,
  languageExplicitlyCleared: boolean,
): boolean {
  if (languageExplicitlyCleared) return !isNonBlankValue(location?.language);
  const stored = location?.language?.trim() ?? '';
  if (stored) return false;
  return false;
}

export function languageOptionalBlank(
  location: LocationGrounding | null,
  languageExplicitlyCleared: boolean,
): OptionalBlankRef | null {
  if (!isLanguageOptionalBlank(location, languageExplicitlyCleared)) return null;
  return { fieldId: WIZARD_QUESTION_IDS.groundingLanguage, label: 'Language' };
}

export function isCrowdOptionalBlank(draft: WorldDraft): boolean {
  const ans = draft.answers[WIZARD_QUESTION_IDS.connectedCrowd];
  return !ans || ans.status === 'unanswered';
}

export function isRhythmOptionalBlank(draft: WorldDraft): boolean {
  const ans = draft.answers[WIZARD_QUESTION_IDS.connectedRhythm];
  return !ans || ans.status === 'unanswered';
}

export function connectedOptionalBlanks(draft: WorldDraft): OptionalBlankRef[] {
  const blanks: OptionalBlankRef[] = [];
  if (isCrowdOptionalBlank(draft)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.connectedCrowd, label: 'Who typically comes here' });
  }
  if (isRhythmOptionalBlank(draft)) {
    blanks.push({ fieldId: WIZARD_QUESTION_IDS.connectedRhythm, label: 'Daily rhythm' });
  }
  return blanks;
}

export function periodRationaleOptionalBlank(period: TimePeriod | null): OptionalBlankRef | null {
  if (period?.rationale?.trim()) return null;
  return { fieldId: WIZARD_QUESTION_IDS.periodRationale, label: 'Talk about this period' };
}

export function optionalBlanksForStep(
  draft: WorldDraft,
  step: OptionalBuilderStep,
  ctx?: { languageExplicitlyCleared?: boolean },
): OptionalBlankRef[] {
  switch (step) {
    case 'location-grounding': {
      const blank = languageOptionalBlank(draft.location, ctx?.languageExplicitlyCleared ?? false);
      return blank ? [blank] : [];
    }
    case 'connected':
      return connectedOptionalBlanks(draft);
    case 'time-period': {
      const blank = periodRationaleOptionalBlank(draft.timePeriod);
      return blank ? [blank] : [];
    }
    default:
      return [];
  }
}

export function fillGroundingLanguage(draft: WorldDraft): WorldDraft {
  const location = draft.location ?? { name: '', region: '', language: '' };
  const value = String(suggestGroundingField(draft.draftId, 'language').value);
  return setLocationGrounding(draft, { ...location, language: value }, { source: 'ai_suggestion' });
}

export function fillConnectedOptional(draft: WorldDraft): WorldDraft {
  let next = draft;
  const connected = draft.connected ?? emptyConnectedAnswers();
  if (isCrowdOptionalBlank(next)) {
    const fb = suggestForQuestion(next, WIZARD_QUESTION_IDS.connectedCrowd);
    const crowd = typeof fb.value === 'string'
      ? patchConnectedCrowd(connected.crowd, fb.value as ConnectedAnswers['crowd']['type'])
      : (fb.value as ConnectedAnswers['crowd']);
    next = setConnected(next, { ...connected, crowd }, { source: fb.source });
  }
  if (isRhythmOptionalBlank(next)) {
    const rhythm = defaultRhythmSuggestion(next.draftId);
    const base = next.connected ?? connected;
    next = setConnected(next, { ...base, rhythm }, { source: 'fallback' });
  }
  return next;
}

export function fillPeriodRationale(draft: WorldDraft): WorldDraft {
  const period = draft.timePeriod ?? { present: DEFAULT_PERIOD_PRESENT, period: DEFAULT_PERIOD_PRESENT, rationale: '' };
  const rationale = suggestTimePeriod(draft.draftId).rationale ?? '';
  return setTimePeriod(draft, trimTimePeriod({ ...period, rationale }));
}

export function fillOptionalBlanksOnStep(
  draft: WorldDraft,
  step: OptionalBuilderStep,
): WorldDraft {
  switch (step) {
    case 'location-grounding':
      return fillGroundingLanguage(draft);
    case 'connected':
      return fillConnectedOptional(draft);
    case 'time-period':
      return fillPeriodRationale(draft);
    default:
      return draft;
  }
}

/** Persist default English when advancing with untouched language display. */
export function persistDefaultGroundingLanguage(draft: WorldDraft): WorldDraft {
  const loc = draft.location;
  if (!loc) return draft;
  if (loc.language.trim()) return draft;
  return setLocationGrounding(draft, { ...loc, language: DEFAULT_GROUNDING_LANGUAGE });
}

export function effectiveGroundingLanguage(
  location: LocationGrounding | null,
  languageExplicitlyCleared: boolean,
): string {
  return displayGroundingLanguage(location, languageExplicitlyCleared);
}

export function groundingLanguageCountsAsFilled(
  location: LocationGrounding | null,
  languageExplicitlyCleared: boolean,
): boolean {
  if (languageExplicitlyCleared) return isNonBlankValue(location?.language);
  const stored = location?.language?.trim() ?? '';
  if (stored) return true;
  return true;
}
